/**
 * Global AI cost-preview gate.
 *
 * Any AI action routed through `invokeAi` asks the mounted
 * <AiCostPreviewDialog /> for confirmation before spending credits.
 * Implemented as a module-level bus so non-React code can await it.
 */

export interface AiCostRequest {
  /** Total credits the action will cost. */
  credits: number;
  /** Human readable feature name shown in the dialog. */
  featureName: string;
  /** Optional extra line explaining what will be produced. */
  detail?: string;
  /** Edge function name, used for analytics/debugging. */
  fn?: string;
}

type Handler = (request: AiCostRequest) => Promise<boolean>;

let handler: Handler | null = null;

export function registerAiCostConfirmHandler(next: Handler): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

/**
 * Returns true when the action may proceed.
 * Falls back to `true` when no dialog is mounted (e.g. overlays, OBS pages).
 */
export async function confirmAiCost(request: AiCostRequest): Promise<boolean> {
  if (!handler) return true;
  if (!request.credits || request.credits <= 0) return true;
  try {
    return await handler(request);
  } catch {
    return false;
  }
}
